import { useApplicantAPI } from "@certn/api";
import { Text, Stack } from "@certn/cascadia";
import {
  Dialog,
  DialogHeader,
  DialogContent,
  DialogFooter,
  PrimaryButton,
  ButtonGroup,
  BasicButton,
  List,
  ListItem,
} from "@certn/cascadia-react";
import { useContext, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { useBiometricIdOptOutHandler } from "./hooks/useBiometricIdOptOutHandler";
import { useApplication } from "../../ApplicationContext";
import { useApplicationFormContext } from "../../ApplicationFormContext";
import { OneIdLayoutContext } from "../../layouts/OneIdLayoutContext";
import { updateSection } from "../../utils";

interface Props {
  skipEligibility: string | undefined;
}

export function SkipBiometricConsent(props: Props) {
  const { caseId } = useApplicationFormContext();

  const { data } = useApplicantAPI(`/api/internal/applicant/case/${caseId}/`);
  const { t } = useTranslation();
  const changeBiometricIdOptOut = useBiometricIdOptOutHandler();
  const { skipEligibility } = props;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { currentSection, nextSection, navigateToSection, basePath } =
    useApplication();

  const { data: checks } = useApplicantAPI(
    `/api/internal/applicant/case/${caseId}/idv-check-list/`
  );
  const { showNonBiometricIdSection } = useContext(OneIdLayoutContext);

  const submitOptOut = async () => {
    setIsSubmitting(true);
    await changeBiometricIdOptOut(true);
    const optOut = {
      "one-id": [{ biometric_id_verification_opt_out: true }],
    };
    try {
      updateSection(caseId, optOut, currentSection, false, basePath);
    } catch (e) {
      console.error("Error changing value of biometric ID opt out", e);
    }
    if (!showNonBiometricIdSection) {
      if (nextSection) {
        navigateToSection(nextSection);
      } else {
        navigateToSection("summary");
      }
    }
    setIsSubmitting(false);
  };

  return (
    <Dialog>
      {({ close }) => (
        <>
          <DialogHeader onClose={close}>
            <Text style="global/modal-header">
              <Trans>Skip Biometric Consent</Trans>
            </Text>
          </DialogHeader>
          <DialogContent>
            <Stack gap="space-4">
              {skipEligibility === "UK_RTW_NO_ALTERNATIVE" ? (
                <Text>
                  {t(
                    `By opting out of Biometric Consent, {{customer_name}} will be in contact to manually verify your right to work in the UK. Are you sure you want to opt out?`,
                    {
                      customer_name: data?.customer_name,
                    }
                  )}
                </Text>
              ) : (
                <Text>
                  {t(
                    `By opting out of Biometric Identity Verification, we may not be able to complete the following services included in your application:`
                  )}
                </Text>
              )}
              {skipEligibility !== "UK_RTW_NO_ALTERNATIVE" && (
                <>
                  <List type="unordered">
                    {checks &&
                      checks?.one_id_check_list &&
                      checks?.one_id_check_list.map(
                        (checkName: string, checkIndex: number) => (
                          <ListItem key={checkIndex}>
                            <Text>{checkName}</Text>
                          </ListItem>
                        )
                      )}
                    <ListItem>
                      <Text>{t("ID Verification")}</Text>
                    </ListItem>
                  </List>

                  <Text>
                    <Trans>Are you sure you want to opt out?</Trans>
                  </Text>
                </>
              )}
            </Stack>
          </DialogContent>
          <DialogFooter>
            <ButtonGroup>
              <BasicButton
                testID="skip-modal-cancel-button"
                size="medium"
                onPress={close}
              >
                <Trans>Cancel</Trans>
              </BasicButton>
              <PrimaryButton
                testID="skip-modal-next-step-button"
                size="medium"
                isDisabled={isSubmitting}
                isLoading={isSubmitting}
                onPress={() => {
                  submitOptOut();
                }}
              >
                {skipEligibility === "UK_RTW_NO_ALTERNATIVE" ? (
                  <Trans>Continue</Trans>
                ) : (
                  <Trans>Opt out</Trans>
                )}
              </PrimaryButton>
            </ButtonGroup>
          </DialogFooter>
        </>
      )}
    </Dialog>
  );
}
